import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  DocumentArrowUpIcon,
  DocumentTextIcon,
  TableCellsIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const fileTypeIcons = {
  'text/csv': TableCellsIcon,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': TableCellsIcon,
  'application/vnd.ms-excel': TableCellsIcon,
  'application/pdf': DocumentTextIcon,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': DocumentTextIcon,
  'text/plain': DocumentTextIcon,
};

export default function FileUpload({ onFilesSelected, acceptedTypes, maxFiles = 10 }) {
  const [files, setFiles] = useState([]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      status: 'pending',
      progress: 0
    }));

    setFiles(prev => [...prev, ...newFiles]);

    if (onFilesSelected) {
      onFilesSelected(acceptedFiles);
    }
  }, [onFilesSelected]);

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: acceptedTypes || {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    maxFiles
  });

  const getFileIcon = (type) => {
    const Icon = fileTypeIcons[type] || DocumentTextIcon;
    return Icon;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive
            ? 'border-blue-500 bg-blue-50'
            : isDragReject
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}
      >
        <input {...getInputProps()} />
        <DocumentArrowUpIcon className={`h-12 w-12 mx-auto mb-4 ${isDragActive ? 'text-blue-500' : 'text-gray-400'
          }`} />
        {isDragActive ? (
          <p className="text-blue-600 font-medium">Drop the files here...</p>
        ) : isDragReject ? (
          <p className="text-red-600 font-medium">Unsupported file type</p>
        ) : (
          <>
            <p className="text-gray-600 font-medium">
              Drag & drop voter data files here
            </p>
            <p className="text-gray-500 text-sm mt-1">
              or click to browse
            </p>
            <p className="text-gray-400 text-xs mt-3">
              Supported formats: CSV, XLSX, XLS, PDF, DOCX, TXT
            </p>
          </>
        )}
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Selected Files</h4>
          {files.map((fileItem) => {
            const FileIcon = getFileIcon(fileItem.file.type);
            return (
              <div
                key={fileItem.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center space-x-3">
                  <FileIcon className="h-8 w-8 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {fileItem.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(fileItem.file.size)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {fileItem.status === 'pending' && (
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                      Pending
                    </span>
                  )}
                  {fileItem.status === 'processing' && (
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                      Processing...
                    </span>
                  )}
                  {fileItem.status === 'complete' && (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  )}
                  {fileItem.status === 'error' && (
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                  )}
                  <button
                    onClick={() => removeFile(fileItem.id)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
